import { Skeleton, Stack, Typography } from "@mui/material";
import { SenecFunctionalIconButton } from "src/components/button/senec-functional-button/SenecFunctionalIconButton";
import { ChevronLeftIcon, ChevronRightIcon } from "src/foundations/icons";
import { StandardDropdown } from "src/newsenec-ui-components";

export type SenecTablePaginationProps = {
    /**
     * The `page` variable stores the current page number.
     */
    page: number;
    /**
     * The number of items to display per page.
     */
    pageSize: number;
    /**
     * An array of numbers representing the available options for the page size.
     */
    pageSizeOptions: number[];
    /**
     * The total count variable represents a numerical value.
     */
    totalCount: number;
    /**
     * Callback function for selecting page size.
     *
     * @param {number} pageSize - The number of items to display per page.
     * @returns {void}
     */
    onSelectPageSize: (pageSize: number) => void;
    /**
     * Callback function called when a page is selected.
     *
     * @param {number} pageSize - The size of the page selected.
     * @returns {void} - It does not return any value.
     */
    onSelectPage: (pageSize: number) => void;
    /**
     * Represents whether the application is currently loading.
     */
    isLoading: boolean;
    /**
     * Represents a string that is used to separate text.
     */
    separatorText: string;
};

export function SenecTablePagination({
    page,
    pageSize,
    totalCount,
    pageSizeOptions,
    onSelectPageSize,
    onSelectPage,
    isLoading,
    separatorText,
}: SenecTablePaginationProps) {
    const isDisabled = (item: string) => {
        if (isLoading) return true;
        if (item === "previous") return page < 1;
        if (item === "next") return pageSize * (page + 1) >= totalCount;
        return false;
    };

    const tablePaginationOptions: string[] = pageSizeOptions.map((option) => option.toString());

    const startPage = page * pageSize + 1;
    const endPage = (page + 1) * pageSize > totalCount ? totalCount : (page + 1) * pageSize;

    return (
        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
            {tablePaginationOptions.length > 0 && (
                <StandardDropdown
                    options={tablePaginationOptions}
                    size={"small"}
                    variant={"filled"}
                    value={pageSize}
                    onChange={(pageSize) => onSelectPageSize(Number(pageSize))}
                    disabled={isLoading}
                />
            )}
            <Stack direction={"row"} alignItems={"center"} gap={1.5}>
                {isLoading && totalCount === 0 && (
                    <Skeleton variant="rounded" sx={{ height: "20px", width: "100px", borderRadius: "12px" }} />
                )}
                {(!isLoading || totalCount !== 0) && (
                    <Typography variant={"s1Medium"}>{`${startPage} - ${endPage} ${separatorText} ${totalCount}`}</Typography>
                )}
                <SenecFunctionalIconButton
                    data-testid="pagination-previous-btn"
                    onClick={() => onSelectPage(page - 1)}
                    level="secondary"
                    disabled={isDisabled("previous")}
                >
                    <ChevronLeftIcon />
                </SenecFunctionalIconButton>
                <SenecFunctionalIconButton
                    data-testid="pagination-next-btn"
                    onClick={() => onSelectPage(page + 1)}
                    level="secondary"
                    disabled={isDisabled("next")}
                >
                    <ChevronRightIcon />
                </SenecFunctionalIconButton>
            </Stack>
        </Stack>
    );
}
